import {
  type PieceRarities,
  type VisibleSlot,
  upgradeColor,
  CHARACTER_CANVAS,
  type Direction,
  getItem,
  bodyLayerOf,
  hairFeatureOf,
  isLayerVisible,
  isBaseLayerVisible,
  isVisibleSlot,
  VISIBLE_SLOTS,
  type Layer,
  layerOrder,
  type StatusId,
  TILE_SIZE,
} from '@shefira/shared';
import Phaser from 'phaser';

import { language } from '../i18n/language.js';
import { uiText } from '../ui/text.js';
import { GM_NAME_COLOR, gmName } from './gm.js';
import { bubbleLines } from './bubble-lines.js';
import {
  healthBarFill,
  type HealthBarStyle,
  OTHER_BAR,
  SELF_BAR,
  SELF_BAR_DEPTH,
} from './health-bar.js';
import { PixelText } from './pixel-text.js';
import { SpeechBubble } from './speech-bubble.js';
import { contentTop, StatusMarks } from './status-marks.js';
import { RareMark } from './rare-mark.js';
import { RarityShine } from './rarity-shine.js';
import { UpgradeShine } from './upgrade-shine.js';

/**
 * A character on screen: one container, one sprite per visible layer, all
 * playing the same animation key in lockstep.
 *
 * Multi-layer from day one even though only body, eyes and weapon exist
 * (ROADMAP, Phase 1), because the layer order and the shared animation grid
 * are exactly what the paper doll needs later.
 */

/**
 * Layers coloured at runtime rather than in the art.
 *
 * A Phaser tint multiplies, so a white silhouette becomes a flat colour (the
 * eyes) while a greyscale one keeps its shading (the hair). Equipment layers
 * take the tint of the item template they show (`items.ts`): one greyscale
 * overlay, several metals.
 *
 * Skin, eye and hair colours are per-character choices (`setLook`); these are the
 * defaults a view shows until its character's look arrives.
 */
const TINTED_LAYERS: Partial<Record<Layer, number>> = {
  skin: 0xffffff,
  eyes: 0x498ace,
  hair: 0x58422f,
};

function toTint(hex: string): number {
  return Number.parseInt(hex.slice(1), 16);
}

/** How long a spoken line hangs over the head. */
const BUBBLE_MS = 5_000;
/** How long a quest marker holds each of its two heights. */
const MARKER_BOB_MS = 450;
/** Empty rows under the marker's glyph on its 32 px canvas, minus a pixel of air. */
const MARKER_FOOT = 3;
/** The aggro « ! » (a monster turned on us): up, held, then gone. */
const ALERT_MS = 1_100;
const ALERT_RISE_MS = 120;
const ALERT_RISE_PX = 3;
const ALERT_FADE_MS = 250;
const ALERT_COLOR = 0xe8383b;
/** Summoning bar (the mount's), just under the feet so it never hides the plate. */
const CHANNEL_Y = 2;
const CHANNEL_COLOR = 0xf0c050;
/** A hit turns the whole character white for this long. */
const FLASH_MS = 90;
/** Animations that play once and hold their last frame. */
const ONE_SHOT = new Set(['attack', 'hurt', 'death']);
/** Death dissolve: every other pixel of the sprite flies off and fades over this long. */
const DISSOLVE_MS = 650;
const DISSOLVE_STEP = 2;
/** A bubble line holds this many letters; a longer line wraps onto more lines. */
const BUBBLE_MAX_CHARS = 28;
/** Past this many lines the bubble is cut; the full line is in the chat log. */
const BUBBLE_MAX_LINES = 4;
/**
 * A player's chat line is shown whole: CHAT_MAX_LENGTH keeps it to two or three lines,
 * and this cap is only there so an odd run of long words still fits.
 */
const CHAT_BUBBLE_MAX_LINES = 4;
/** A long line stays up longer, so it can be read to the end. */
const BUBBLE_MS_PER_LINE = 1_500;
/** Blank columns between the level and the name. */
const LEVEL_GAP = 3;
/** The quest's « ! » before a hunted creature's level, its rim included: two pixels of air after it. */
const HUNT_MARK_WIDTH = 3;
/** The level reads in gold, like the level-up text, apart from the name. */
const LEVEL_COLOR = 0xf0c050;
/** A worn title (ADR 0046): parchment gold, paler than the level so the two never merge. */
const TITLE_COLOR = 0xe8d08a;
/** Away from the keyboard (ADR 0080): a muted grey that reads as « not here », never as a title. */
const AWAY_COLOR = 0x9a9aae;

export interface CharacterViewOptions {
  scene: Phaser.Scene;
  /** Logical body key, e.g. `body.warrior`. */
  bodyKey: string;
  atlasKey: string;
  name: string;
  /** The local player (SHE-35): its health bar stands out and is drawn over everyone. */
  self?: boolean;
  /**
   * The golden ring turning over the halo at +10 (ADR 0013): its looping clip
   * and atlas; without it, no ring.
   */
  halo?: { atlasKey: string; clip: string; firstFrame: string };
  /** Frames present in the atlas, so a missing layer is simply not drawn. */
  hasFrame: (frameName: string) => boolean;
  /** Atlas and key of the bitmap font used for speech and, by default, the name plate. */
  font: { atlasKey: string; fontKey: string };
  /** Name plate style: the compact font, in the colour that sorts friend from foe. */
  label?: {
    atlasKey: string;
    fontKey: string;
    glyphWidth: number;
    glyphHeight: number;
    color: number;
    /**
     * Health bar inside the canvas's top rows, where a low monster leaves them
     * empty; otherwise the plate stands above the canvas, for a head that fills it.
     */
    overHead?: boolean;
  };
}

/** How a vanished game master sees themselves: there, but plainly not there for the others. */
const VANISHED_ALPHA = 0.45;

export class CharacterView {
  readonly container: Phaser.GameObjects.Container;
  private readonly sprites = new Map<Layer, Phaser.GameObjects.Sprite>();
  /** The shine of upgraded pieces (ADR 0013), once one is worn. */
  private shine?: UpgradeShine;
  /** The sheen, outline and motes of rare pieces (ADR 0061), once one is worn. */
  private rarityShine?: RarityShine;
  private rarities: PieceRarities = {};
  /** The jade outline of a wandering rare (ADR 0094); absent on anyone else. */
  private rareMark?: RareMark;
  private raritiesKey = '';
  private readonly label: PixelText;
  /** The level, left of the name; empty for the peaceful and for pets. */
  private readonly levelText: PixelText;
  /** A quest's « ! » before the level of a creature to hunt (ADR 0047). */
  private readonly huntMark: Phaser.GameObjects.Container;
  private hunted = false;
  private alert: { mark: Phaser.GameObjects.Container; tween: Phaser.Tweens.Tween } | null = null;
  /** The worn title, a line above the name; empty for none. */
  private readonly titleText: PixelText;
  private title = '';
  /** Away (ADR 0080): « Absent » takes the title's line until the player is back. */
  private away = false;
  private vanished = false;
  private level = 0;
  /** A game master: `[GM]` before the name, and the plate in violet over any aura. */
  private gm = false;
  /** The guild's tag, shown after the name; empty without a guild. */
  private guildTag = '';
  private aura = 0;
  private readonly bubble: SpeechBubble;
  private bubbleTimer?: Phaser.Time.TimerEvent;
  /** This character's health bar geometry (SHE-35): the player's stands out. */
  private readonly barStyle: HealthBarStyle;
  private readonly barBack: Phaser.GameObjects.Rectangle;
  private readonly barFill: Phaser.GameObjects.Rectangle;
  /**
   * The local player's bar, out of the character's container so it draws over
   * everyone, at a fixed depth (SHE-35); null for everyone else.
   */
  private readonly barLayer: Phaser.GameObjects.Container | null;
  private readonly channelBack: Phaser.GameObjects.Rectangle;
  private readonly channelFill: Phaser.GameObjects.Rectangle;
  private flashTimer?: Phaser.Time.TimerEvent;
  private freezeTimer?: Phaser.Time.TimerEvent;
  private dead = false;
  private readonly particles: Phaser.GameObjects.Rectangle[] = [];
  private readonly options: CharacterViewOptions;
  private animation = '';
  private direction: Direction | null = null;
  /** Visible slots currently worn (template ids); a bare slot hides its layer. */
  private appearance: Partial<Record<Layer, string>> = {};
  /** Skin, hair and eye tints of this character, as CSS hex. */
  private look: {
    hair: string;
    eyes: string;
    skin: string;
    hairStyle: string;
    body: string;
  } | null = null;
  /** The mount under the character, drawn on its own canvas; null on foot. */
  private mount: {
    sprite: Phaser.GameObjects.Sprite;
    prefix: string;
    seat: number;
    seatBack: number;
  } | null = null;
  /** A quest's « ! » or « ? » over the plate (ADR 0047), and its bob. */
  private marker: {
    sprite: Phaser.GameObjects.Sprite;
    key: string;
    timer: Phaser.Time.TimerEvent;
  } | null = null;
  /** Where the plate, the bubble and the bar sit on foot; a mount lifts them by its seat. */
  private readonly plateY: { label: number; bubble: number; bar: number; title: number };
  /** The timed states of ADR 0048 (stars, frost, drops…), drawn only while one is on. */
  private readonly statuses: StatusMarks;
  /** Where the name plate ends, for the taunt's « ! » after it. */
  private plateEnd = { x: 0, y: 0 };

  constructor(options: CharacterViewOptions) {
    this.options = options;
    this.barStyle = options.self === true ? SELF_BAR : OTHER_BAR;
    const { scene } = options;
    this.container = scene.add.container(0, 0);

    // Base layers exist only when the atlas has art for them; equipment
    // layers always exist, hidden until an item is worn, because which frames
    // they show depends on the item and can change at any time.
    const placeholder = `${options.bodyKey}/body/idle/south/0`;
    for (const layer of [...layerOrder('south'), ...layerOrder('north')]) {
      if (this.sprites.has(layer)) continue;
      let start = placeholder;
      if (!isVisibleSlot(layer)) {
        const south = this.frameName(layer, 'idle', 'south', 0);
        const north = this.frameName(layer, 'idle', 'north', 0);
        if (!options.hasFrame(south) && !options.hasFrame(north)) continue;
        start = options.hasFrame(south) ? south : north;
      }
      const sprite = scene.add.sprite(0, 0, options.atlasKey, start).setOrigin(0.5, 1);
      const tint = TINTED_LAYERS[layer];
      if (tint !== undefined) sprite.setTint(tint);
      if (isVisibleSlot(layer)) sprite.setVisible(false);
      this.container.add(sprite);
      this.sprites.set(layer, sprite);
    }

    // Most bodies share the 32 px canvas; a tall monster (goblin, yeti) stands
    // on a bigger one with the same foot line, so the plate goes above ITS top.
    const canvas = options.hasFrame(placeholder)
      ? scene.textures.getFrame(options.atlasKey, placeholder).height
      : CHARACTER_CANVAS;

    const label = options.label;
    this.label = new PixelText({
      scene,
      atlasKey: label?.atlasKey ?? options.font.atlasKey,
      fontKey: label?.fontKey ?? options.font.fontKey,
      glyphWidth: label?.glyphWidth,
      glyphHeight: label?.glyphHeight,
      color: label?.color ?? 0xe6e6f0,
      outlineColor: 0x12121c,
    });
    this.label.setText(options.name);
    this.levelText = new PixelText({
      scene,
      atlasKey: label?.atlasKey ?? options.font.atlasKey,
      fontKey: label?.fontKey ?? options.font.fontKey,
      glyphWidth: label?.glyphWidth,
      glyphHeight: label?.glyphHeight,
      color: LEVEL_COLOR,
      outlineColor: 0x12121c,
    });
    // Top to bottom: name, bar, head. Glyphs are drawn downward from their
    // position and carry a one-pixel outline, so the bar starts one row under
    // the outline. A monster's bar dips into the empty rows over its head; a
    // player's head reaches the canvas top, so its bar clears it by one row.
    const labelHeight = this.label.glyphHeight;
    const barY = label?.overHead === true ? -canvas : -(canvas + this.barStyle.height + 1);
    const labelY = barY - labelHeight - 1;
    this.titleText = new PixelText({
      scene,
      atlasKey: label?.atlasKey ?? options.font.atlasKey,
      fontKey: label?.fontKey ?? options.font.fontKey,
      glyphWidth: label?.glyphWidth,
      glyphHeight: label?.glyphHeight,
      color: TITLE_COLOR,
      outlineColor: 0x12121c,
    });
    this.container.add([this.label.container, this.levelText.container, this.titleText.container]);
    // A quest's « ! » before the level, on whole pixels, the plate's outline around it.
    const rim = 0x12121c;
    const gold = 0xffc82e;
    this.huntMark = scene.add.container(0, 0, [
      scene.add.rectangle(-1, -1, 3, 5, rim).setOrigin(0, 0),
      scene.add.rectangle(-1, 3, 3, 3, rim).setOrigin(0, 0),
      scene.add.rectangle(0, 0, 1, 3, gold).setOrigin(0, 0),
      scene.add.rectangle(0, 4, 1, 1, gold).setOrigin(0, 0),
    ]);
    this.huntMark.setVisible(false);
    this.container.add(this.huntMark);

    this.bubble = new SpeechBubble({
      scene,
      atlasKey: options.font.atlasKey,
      fontKey: options.font.fontKey,
    });
    // The point's last pixel, clear of the name's outline row.
    const bubbleY = labelY - 3;
    this.bubble.setPosition(0, bubbleY);
    this.bubble.setVisible(false);
    this.container.add(this.bubble.container);

    // Health bar: two rectangles on whole pixels, hidden at full health like
    // BrowserQuest, so a peaceful meadow is not covered in green bars.
    this.barBack = scene.add
      .rectangle(
        -this.barStyle.width / 2,
        barY,
        this.barStyle.width,
        this.barStyle.height,
        this.barStyle.rim,
      )
      .setOrigin(0, 0)
      .setVisible(false);
    this.barFill = scene.add
      .rectangle(
        -this.barStyle.width / 2 + 1,
        barY + 1,
        this.barStyle.width - 2,
        this.barStyle.height - 2,
        0x9ece6a,
      )
      .setOrigin(0, 0)
      .setVisible(false);
    this.plateY = { label: labelY, bubble: bubbleY, bar: barY, title: labelY - labelHeight - 2 };
    if (options.self === true) {
      // Out of the character's container: its depth is its feet, and any
      // monster lower on screen would be painted over the bar.
      this.barLayer = scene.add
        .container(0, 0, [this.barBack, this.barFill])
        .setDepth(SELF_BAR_DEPTH);
    } else {
      this.barLayer = null;
      this.container.add([this.barBack, this.barFill]);
    }
    this.channelBack = scene.add
      .rectangle(-OTHER_BAR.width / 2, CHANNEL_Y, OTHER_BAR.width, OTHER_BAR.height, 0x12121c)
      .setOrigin(0, 0)
      .setVisible(false);
    this.channelFill = scene.add
      .rectangle(-OTHER_BAR.width / 2 + 1, CHANNEL_Y + 1, 1, OTHER_BAR.height - 2, CHANNEL_COLOR)
      .setOrigin(0, 0)
      .setVisible(false);
    this.container.add([this.channelBack, this.channelFill]);
    const bodyTop = options.hasFrame(placeholder)
      ? contentTop(scene, options.atlasKey, placeholder)
      : 0;
    this.statuses = new StatusMarks(scene, {
      headY: () => -(canvas - bodyTop) - (this.mount?.seat ?? 0),
      plateEnd: () => this.plateEnd,
    });
    this.container.add(this.statuses.graphics);
    this.placePlate(0);
  }

  /** A timed state landed on this character (ADR 0048), for `durationMs` of the scene's clock. */
  setStatus(status: StatusId, durationMs: number): void {
    if (this.dead) return;
    this.statuses.set(status, durationMs);
    this.container.bringToTop(this.statuses.graphics);
  }

  /** The level, shown before the name; 0 leaves the plate as the bare name. */
  setLevel(level: number): void {
    if (level === this.level) return;
    this.level = level;
    this.levelText.setText(level > 0 ? String(level) : '');
    this.placePlate(-(this.mount?.seat ?? 0));
  }

  setGm(gm: boolean): void {
    if (gm === this.gm) return;
    this.gm = gm;
    this.label.setText(this.plateName());
    this.label.setColor(this.plateColor());
    this.placePlate(-(this.mount?.seat ?? 0));
  }

  /** The guild's tag after the name (ADR 0036); empty without a guild. */
  setGuild(tag: string): void {
    if (tag === this.guildTag) return;
    this.guildTag = tag;
    this.label.setText(this.plateName());
    this.placePlate(-(this.mount?.seat ?? 0));
  }

  /** The worn title's French (ADR 0046), a line above the name; '' takes it off. */
  setTitle(title: string): void {
    if (title === this.title) return;
    this.title = title;
    this.showTitleLine();
  }

  /** Away from the keyboard (ADR 0080): « Absent » over the name, in place of the title. */
  /** A game master out of sight (ADR 0025): only they are sent it, and they see themselves faded. */
  setVanished(vanished: boolean): void {
    if (vanished === this.vanished) return;
    this.vanished = vanished;
    this.container.setAlpha(vanished ? VANISHED_ALPHA : 1);
  }

  setAway(away: boolean): void {
    if (away === this.away) return;
    this.away = away;
    this.showTitleLine();
  }

  private showTitleLine(): void {
    // The bitmap font keeps French accents; English is looked up (ADR 0069).
    const away = language() === 'en' ? uiText('Absent') : 'Absent';
    this.titleText.setColor(this.away ? AWAY_COLOR : TITLE_COLOR);
    this.titleText.setText(this.away ? away : this.title);
    this.placePlate(-(this.mount?.seat ?? 0));
  }

  /** `Nom [TAG]`, and `[GM]` before it for a game master. */
  private plateName(): string {
    const name = this.options.name;
    return gmName(this.guildTag.length > 0 ? `${name}[${this.guildTag}]` : name, this.gm);
  }

  private plateColor(): number {
    if (this.gm) return GM_NAME_COLOR;
    // The name shows the aura too, from +3: proof of the outfit at a glance.
    return upgradeColor(this.aura) ?? this.options.label?.color ?? 0xe6e6f0;
  }

  /**
   * Centre level and name together as one line. Each PixelText centres itself
   * on its position, so each is placed at its own centre, on whole pixels.
   */
  private placePlate(lift: number): void {
    const y = this.plateY.label + lift;
    const hunt = this.huntMark.visible ? HUNT_MARK_WIDTH + LEVEL_GAP : 0;
    const levelWidth = this.levelText.width;
    const nameWidth = this.label.width;
    const gap = levelWidth > 0 && nameWidth > 0 ? LEVEL_GAP : 0;
    const start = -Math.round((hunt + levelWidth + gap + nameWidth) / 2);
    // The quest's « ! » opens the line, before the level (ADR 0047).
    this.huntMark.setPosition(start + 1, y);
    const left = start + hunt;
    this.levelText.setPosition(left + Math.round(levelWidth / 2), y);
    this.titleText.setPosition(0, this.plateY.title + lift);
    // The chat bubble climbs over the title, never onto it.
    const titled = this.titleText.width > 0 ? this.plateY.label - this.plateY.title : 0;
    this.bubble.setPosition(0, this.plateY.bubble - titled + lift);
    this.label.setPosition(left + levelWidth + gap + Math.round(nameWidth / 2), y);
    this.plateEnd = { x: left + levelWidth + gap + nameWidth + 3, y: y + 1 };
    this.placeMarker(lift);
  }

  /** A wandering rare (ADR 0094): its body outlined in the rares' jade. */
  setRare(rare: boolean): void {
    if (rare === (this.rareMark !== undefined)) return;
    if (!rare) {
      this.rareMark?.destroy();
      this.rareMark = undefined;
      return;
    }
    this.rareMark = new RareMark(this.options.scene, () => this.sprites.values());
    this.container.add(this.rareMark.front);
    this.rareMark.setVisible(!this.dead);
    this.direction = null;
  }

  /**
   * A creature a quest under way asks for (ADR 0047): a small golden « ! »
   * before its level, so the player sees which ones count.
   */
  setHunted(hunted: boolean): void {
    if (hunted === this.hunted) return;
    this.hunted = hunted;
    this.huntMark.setVisible(hunted && !this.dead);
    this.placePlate(-(this.mount?.seat ?? 0));
  }

  /**
   * A monster has just turned on the local player: a red « ! » springs up
   * over its plate, stays a moment and fades. Called again, it starts over.
   */
  alertAggro(): void {
    if (this.dead) return;
    this.clearAlert();
    const { scene } = this.options;
    const rim = 0x12121c;
    // Drawn upward from its foot at 0: a 2 px bar, a row of air, a 2 px dot, rimmed.
    const mark = scene.add.container(0, 0, [
      scene.add.rectangle(-2, -9, 4, 7, rim).setOrigin(0, 0),
      scene.add.rectangle(-2, -3, 4, 4, rim).setOrigin(0, 0),
      scene.add.rectangle(-1, -8, 2, 5, ALERT_COLOR).setOrigin(0, 0),
      scene.add.rectangle(-1, -2, 2, 2, ALERT_COLOR).setOrigin(0, 0),
    ]);
    this.container.add(mark);
    const top = this.titleText.width > 0 ? this.plateY.title : this.plateY.label;
    const foot = top - 1 - (this.mount?.seat ?? 0);
    const clock = { ms: 0 };
    const tween = scene.tweens.add({
      targets: clock,
      ms: ALERT_MS,
      duration: ALERT_MS,
      onUpdate: () => {
        const rise = Math.min(1, clock.ms / ALERT_RISE_MS);
        mark.setPosition(0, foot + Math.round((1 - rise) * ALERT_RISE_PX));
        const left = ALERT_MS - clock.ms;
        mark.setAlpha(left < ALERT_FADE_MS ? left / ALERT_FADE_MS : 1);
      },
      onComplete: () => {
        this.clearAlert();
      },
    });
    mark.setPosition(0, foot + ALERT_RISE_PX);
    this.alert = { mark, tween };
  }

  private clearAlert(): void {
    this.alert?.tween.remove();
    this.alert?.mark.destroy();
    this.alert = null;
  }

  /**
   * A quest's marker over the plate (ADR 0047): the frame of an effect in its
   * atlas, tinted grey for a quest under way; null takes it off. It bobs a
   * pixel up and down, on whole pixels, so it catches the eye from afar.
   */
  setQuestMarker(marker: { atlasKey: string; frame: string; tint?: number } | null): void {
    const key = marker === null ? '' : `${marker.frame}:${String(marker.tint ?? '')}`;
    if (key === (this.marker?.key ?? '')) return;
    this.marker?.timer.remove(false);
    this.marker?.sprite.destroy();
    this.marker = null;
    if (marker === null) return;
    const { scene } = this.options;
    const sprite = scene.add.sprite(0, 0, marker.atlasKey, marker.frame).setOrigin(0.5, 1);
    if (marker.tint !== undefined) sprite.setTint(marker.tint);
    this.container.add(sprite);
    const timer = scene.time.addEvent({
      delay: MARKER_BOB_MS,
      loop: true,
      callback: () => {
        sprite.setData('bob', sprite.getData('bob') === 1 ? 0 : 1);
        this.placeMarker(-(this.mount?.seat ?? 0));
      },
    });
    this.marker = { sprite, key, timer };
    this.placeMarker(-(this.mount?.seat ?? 0));
  }

  /** Over the title when one is worn, over the name otherwise. */
  private placeMarker(lift: number): void {
    const sprite = this.marker?.sprite;
    if (sprite === undefined) return;
    const top = this.titleText.width > 0 ? this.plateY.title : this.plateY.label;
    const bob = sprite.getData('bob') === 1 ? 1 : 0;
    // The marker's canvas keeps a few empty rows under its glyph: sit it on the plate.
    sprite.setPosition(0, top + lift + MARKER_FOOT - bob);
  }

  /**
   * What the character wears on its visible slots. A layer whose slot is bare
   * is hidden even when the atlas has art for it; a change replays the pose so
   * the layers refresh.
   */
  setAppearance(appearance: Partial<Record<Layer, string>>): void {
    const changed = VISIBLE_SLOTS.some(
      (slot) => (appearance[slot] ?? '') !== (this.appearance[slot] ?? ''),
    );
    if (!changed) return;
    this.appearance = { ...appearance };
    for (const [layer, sprite] of this.sprites) {
      if (!isVisibleSlot(layer)) continue;
      const tint = this.tintOf(layer);
      if (tint === undefined) sprite.clearTint();
      else sprite.setTint(tint);
    }
    this.setRarities(this.rarities);
    // Force play() to re-evaluate frames and visibility on the next call.
    this.animation = '';
  }

  /**
   * The rarity of each real piece worn (ADR 0061): a sheen, an outline, motes
   * (`rarity-shine.ts`). Created on the first non-common piece, so a plain
   * crowd costs nothing.
   */
  setRarities(rarities: PieceRarities): void {
    const worn: Partial<Record<VisibleSlot, string>> = {};
    for (const slot of VISIBLE_SLOTS) {
      const id = this.appearance[slot];
      if (id !== undefined) worn[slot] = id;
    }
    // Called every frame by the scene: only a change reaches the shine.
    const key = VISIBLE_SLOTS.map(
      (slot) => `${slot === 'wings' ? '' : (rarities[slot] ?? '')}:${worn[slot] ?? ''}`,
    ).join('|');
    if (key === this.raritiesKey) return;
    this.raritiesKey = key;
    this.rarities = rarities;
    if (this.rarityShine === undefined) {
      const shine = new RarityShine({
        scene: this.options.scene,
        pieceOf: (slot) => this.sprites.get(slot),
        layersAbove: (slot) => this.layersAbove(slot),
        layers: () => this.sprites.values(),
        mount: () => this.mount?.sprite,
      });
      shine.set(rarities, worn);
      if (shine.empty) {
        shine.destroy();
        return;
      }
      this.rarityShine = shine;
      this.container.add(shine.front);
      shine.setVisible(!this.dead);
      this.direction = null;
      return;
    }
    this.rarityShine.set(rarities, worn);
  }

  private layersAbove(slot: VisibleSlot): Phaser.GameObjects.Sprite[] {
    const piece = this.sprites.get(slot);
    if (piece === undefined) return [];
    const at = this.container.getIndex(piece);
    return [...this.sprites.values()].filter((sprite) => this.container.getIndex(sprite) > at);
  }

  /**
   * How far the smith took each visible piece, and the outfit's average
   * (ADR 0013): stars on the pieces, a halo and at +10 a crown for the whole
   * outfit (`upgrade-shine.ts`). Created on the first upgraded piece, so a
   * plain crowd costs nothing.
   */
  setUpgrades(levels: Partial<Record<VisibleSlot, number>>, aura: number): void {
    this.aura = aura;
    this.label.setColor(this.plateColor());
    if (this.shine === undefined) {
      if (aura === 0 && VISIBLE_SLOTS.every((slot) => (levels[slot] ?? 0) === 0)) return;
      this.shine = new UpgradeShine({
        scene: this.options.scene,
        pieceOf: (slot) => this.sprites.get(slot),
        crown: this.options.halo,
      });
      this.container.addAt(this.shine.back, 0);
      this.container.add(this.shine.front);
      this.shine.setVisible(!this.dead);
      // Replay the pose so the layer order puts the stars in front.
      this.animation = '';
      this.direction = null;
    }
    this.shine.set(levels, aura);
  }

  /**
   * Put the character on a mount, or back on its feet.
   *
   * The mount is a sprite of its own, on its own canvas, under the body; the
   * body rises by the seat height so the rider sits on the back rather than
   * standing through it, and plays its `ride` clips, which leave the legs out.
   * The plate, the bubble and the bar rise with the rider. Returns whether
   * anything changed, so the scene can mark the moment.
   */
  setMount(
    mount: { atlas: string; prefix: string; seat: number; seatBack?: number } | null,
  ): boolean {
    if (this.mount?.prefix === mount?.prefix) return false;
    this.mount?.sprite.destroy();
    this.mount = null;
    if (mount !== null) {
      const sprite = this.options.scene.add
        .sprite(0, 0, mount.atlas, `${mount.prefix}/body/idle/south/0`)
        .setOrigin(0.5, 1);
      this.container.addAt(sprite, 0);
      this.mount = {
        sprite,
        prefix: mount.prefix,
        seat: mount.seat,
        seatBack: mount.seatBack ?? 0,
      };
    }
    const lift = -(mount?.seat ?? 0);
    for (const sprite of this.sprites.values()) sprite.y = lift;
    this.placePlate(lift);
    this.barBack.y = this.plateY.bar + lift;
    this.barFill.y = this.plateY.bar + lift + 1;
    // Replay: the body swaps between its walking and its riding clips.
    this.animation = '';
    this.direction = null;
    return true;
  }

  /** The body's clip for an animation: on a mount, idle and walk become the riding poses. */
  private bodyAnimation(animation: string): string {
    if (this.mount === null) return animation;
    if (animation === 'idle') return 'ride';
    if (animation === 'walk') return 'ride-walk';
    return animation;
  }

  /** This character's skin, hair and eye colours; the layers are retinted at once. */
  setLook(look: {
    hair: string;
    eyes: string;
    skin: string;
    hairStyle: string;
    body: string;
  }): void {
    if (
      this.look?.hair === look.hair &&
      this.look.eyes === look.eyes &&
      this.look.hairStyle === look.hairStyle &&
      this.look.body === look.body &&
      this.look.skin === look.skin
    )
      return;
    const restyled = this.look?.hairStyle !== look.hairStyle || this.look.body !== look.body;
    this.look = { ...look };
    this.sprites.get('hair')?.setTint(toTint(look.hair));
    this.sprites.get('eyes')?.setTint(toTint(look.eyes));
    this.sprites.get('skin')?.setTint(toTint(look.skin));
    this.sprites.get('hair')?.setVisible(this.slotWorn('hair'));
    // Another cut is another clip: force play() to pick it up.
    if (restyled) this.animation = '';
  }

  private slotWorn(layer: Layer): boolean {
    if (layer === 'body' || layer === 'skin' || layer === 'eyes' || layer === 'hair') {
      return (
        isBaseLayerVisible(layer, this.appearance) &&
        (layer !== 'hair' || hairFeatureOf(this.look?.hairStyle ?? '') !== null)
      );
    }
    return (this.appearance[layer] ?? '') !== '';
  }

  /** Update the health bar. Shown only once hurt; red under a third. */
  setHealth(hp: number, maxHp: number): void {
    const { shown, fillWidth, color } = healthBarFill(hp, maxHp, this.barStyle);
    this.barBack.setVisible(shown);
    this.barFill.setVisible(shown && hp > 0);
    if (!shown) return;
    this.barFill.width = fillWidth;
    this.barFill.setFillStyle(color);
  }

  /** Progress of a summoning under way, 0 to 1; null hides the bar. */
  setChannel(progress: number | null): void {
    this.channelBack.setVisible(progress !== null);
    this.channelFill.setVisible(progress !== null && progress > 0);
    if (progress === null) return;
    const ratio = Math.max(0, Math.min(1, progress));
    this.channelFill.width = Math.max(1, Math.round((OTHER_BAR.width - 2) * ratio));
  }

  /** Hit feedback: every layer goes white for a moment, then its tint comes back. */
  flash(): void {
    for (const sprite of this.sprites.values()) sprite.setTintFill(0xffffff);
    this.flashTimer?.remove(false);
    this.flashTimer = this.options.scene.time.delayedCall(FLASH_MS, () => {
      for (const [layer, sprite] of this.sprites) {
        sprite.clearTint();
        const tint = this.tintOf(layer);
        if (tint !== undefined) sprite.setTint(tint);
      }
    });
  }

  /**
   * The hit stop (ADR 0049): every layer, mount included, holds its frame for
   * a beat, so a critical blow lands with weight. The clock is slowed rather
   * than the clip paused: a pose changed meanwhile stays held too.
   */
  freeze(ms: number): void {
    const sprites = (): Phaser.GameObjects.Sprite[] => [
      ...this.sprites.values(),
      ...(this.mount === null ? [] : [this.mount.sprite]),
    ];
    for (const sprite of sprites()) sprite.anims.timeScale = 0;
    this.freezeTimer?.remove(false);
    this.freezeTimer = this.options.scene.time.delayedCall(ms, () => {
      for (const sprite of sprites()) sprite.anims.timeScale = 1;
    });
  }

  /**
   * Show a spoken line over the head for a few seconds, BrowserQuest style.
   * `chat`: a player talking, never cut (the chat's length cap keeps it short).
   */
  say(text: string, { chat = false }: { chat?: boolean } = {}): void {
    const lines = chat
      ? bubbleLines(text, BUBBLE_MAX_CHARS, CHAT_BUBBLE_MAX_LINES)
      : bubbleLines(text, BUBBLE_MAX_CHARS, BUBBLE_MAX_LINES);
    this.bubble.setText(lines.join('\n'));
    this.placePlate(-(this.mount?.seat ?? 0));
    this.bubble.setVisible(true);
    this.container.bringToTop(this.bubble.container);

    this.bubbleTimer?.remove(false);
    const ms = BUBBLE_MS + (lines.length - 1) * BUBBLE_MS_PER_LINE;
    this.bubbleTimer = this.options.scene.time.delayedCall(ms, () => {
      this.bubble.setVisible(false);
    });
  }

  /**
   * Frame of a layer: base layers are named by the layer, equipment layers by
   * the overlay sprite of the item worn there (`overlay.sword-short`), so the
   * same slot can show any item the rig composed.
   */
  private frameName(layer: Layer, animation: string, direction: Direction, index: number): string {
    return `${this.clipPrefix(layer)}/${animation}/${direction}/${String(index)}`;
  }

  private clipPrefix(layer: Layer): string {
    if (layer === 'hair')
      return `${this.options.bodyKey}/${hairFeatureOf(this.look?.hairStyle ?? '') ?? 'hair'}`;
    // Only players have a look; a monster's body is its own and has no variant.
    if ((layer === 'body' || layer === 'skin') && this.look !== null)
      return `${this.options.bodyKey}/${bodyLayerOf(layer, this.look.body)}`;
    if (!isVisibleSlot(layer)) return `${this.options.bodyKey}/${layer}`;
    const sprite = getItem(this.appearance[layer] ?? '')?.sprite;
    return `${this.options.bodyKey}/${sprite ?? layer}`;
  }

  /** The tint a layer wears: the item's for equipment, the default for features. */
  private tintOf(layer: Layer): number | undefined {
    if (isVisibleSlot(layer)) {
      const hex = getItem(this.appearance[layer] ?? '')?.tint;
      return hex === undefined ? undefined : toTint(hex);
    }
    if (this.look !== null && (layer === 'hair' || layer === 'eyes' || layer === 'skin'))
      return toTint(this.look[layer]);
    return TINTED_LAYERS[layer];
  }

  /**
   * Move the whole character. Coordinates are rounded here rather than in the
   * caller so no layer can ever land on a half pixel (CLAUDE.md rule 6).
   */
  setPixelPosition(px: number, py: number): void {
    this.container.setPosition(Math.round(px), Math.round(py));
    this.barLayer?.setPosition(this.container.x, this.container.y);
  }

  setDepth(depth: number): void {
    this.container.setDepth(depth);
  }

  /**
   * Point every layer at the same animation and direction, then reorder and
   * hide layers to match the direction: facing away, held items pass behind
   * the body and the eyes are not drawn at all.
   */
  play(animation: string, direction: Direction): void {
    // Death is not a pose here: the sprite dissolves and stays gone until revived.
    if (animation === 'death') {
      this.dissolve();
      return;
    }
    if (this.dead) this.revive();
    if (animation === this.animation && direction === this.direction) return;
    const directionChanged = direction !== this.direction;
    this.animation = animation;
    this.direction = direction;

    const mount = this.mount;
    if (mount !== null) {
      // A mount is a body of its own: its frames sit under its `body` layer.
      const clip = `${mount.prefix}/body/${animation === 'walk' ? 'walk' : 'idle'}/${direction}`;
      // The rider stays on the tile; seen from the side the beast steps
      // forward under them, so they sit back on its saddle.
      mount.sprite.x =
        direction === 'east' ? mount.seatBack : direction === 'west' ? -mount.seatBack : 0;
      // The beast lives in the creatures' atlas, not the rider's class atlas.
      if (mount.sprite.texture.has(`${clip}/0`) && this.options.scene.anims.exists(clip)) {
        mount.sprite.setVisible(true);
        mount.sprite.play(clip, true);
      } else {
        mount.sprite.setVisible(false);
      }
    }
    const bodyAnimation = this.bodyAnimation(animation);
    for (const [layer, sprite] of this.sprites) {
      const clip = this.clipFor(layer, bodyAnimation, direction);
      // A layer with no art for this direction must be hidden, not left showing
      // whichever frame it happened to hold — that is how eyes ended up on the
      // back of the head.
      const visible =
        isLayerVisible(layer, direction) &&
        this.slotWorn(layer) &&
        this.options.hasFrame(`${clip}/0`);
      sprite.setVisible(visible);
      if (!visible) continue;
      if (!this.options.scene.anims.exists(clip)) continue;
      // A one-shot restarts even if it is the current clip: two hits in a row
      // must read as two swings, not one long pose.
      sprite.play(clip, !ONE_SHOT.has(animation));
    }

    if (directionChanged) this.applyLayerOrder(direction);
  }

  /**
   * The clip a layer plays. A body without art for a one-shot (a boar has a
   * walk but no strike) lunges with its walk, then stands, rather than
   * vanishing mid-fight; an overlay without art stays hidden, as above.
   */
  private clipFor(layer: Layer, animation: string, direction: Direction): string {
    const clip = `${this.clipPrefix(layer)}/${animation}/${direction}`;
    if (layer !== 'body' || this.options.hasFrame(`${clip}/0`)) return clip;
    for (const fallback of animation === 'attack' ? ['walk', 'idle'] : ['idle']) {
      const other = `${this.clipPrefix(layer)}/${fallback}/${direction}`;
      if (this.options.hasFrame(`${other}/0`)) return other;
    }
    return clip;
  }

  /** Reorder the container's children so the draw order matches the rules. */
  private applyLayerOrder(direction: Direction): void {
    for (const layer of layerOrder(direction)) {
      const sprite = this.sprites.get(layer);
      if (sprite !== undefined) this.container.bringToTop(sprite);
    }
    // The mount is behind the rider — except facing the camera, where its
    // head and neck are nearer than the rider's lap and pass in front.
    if (this.mount !== null) {
      if (direction === 'south') this.container.bringToTop(this.mount.sprite);
      else this.container.sendToBack(this.mount.sprite);
    }
    // The halo stays behind everything, the mount included; the stars sparkle
    // over every layer.
    if (this.rarityShine !== undefined) this.container.bringToTop(this.rarityShine.front);
    if (this.rareMark !== undefined) this.container.bringToTop(this.rareMark.front);
    if (this.shine !== undefined) {
      this.container.sendToBack(this.shine.back);
      this.container.bringToTop(this.shine.front);
    }
    this.container.bringToTop(this.statuses.graphics);
    // The name plate, the bar and the bubble always sit above the character.
    this.container.bringToTop(this.label.container);
    this.container.bringToTop(this.levelText.container);
    this.container.bringToTop(this.titleText.container);
    if (this.barLayer === null) {
      this.container.bringToTop(this.barBack);
      this.container.bringToTop(this.barFill);
    }
    this.container.bringToTop(this.bubble.container);
  }

  /**
   * Die: the sprite breaks into its own pixels, which scatter and fade.
   *
   * Sampled from the frames on screen rather than a generic puff, so a rat
   * dissolves in rat colours and a warrior in skin and cloth; every other
   * pixel keeps the count around forty per character.
   */
  dissolve(): void {
    if (this.dead) return;
    this.dead = true;
    this.clearAlert();
    const scene = this.options.scene;
    const origin = { x: this.container.x, y: this.container.y };
    const depth = this.container.depth + 1;

    for (const sprite of this.sprites.values()) {
      if (!sprite.visible) continue;
      const frame = sprite.frame;
      const tint = sprite.isTinted ? Phaser.Display.Color.IntegerToColor(sprite.tintTopLeft) : null;
      for (let y = 0; y < frame.height; y += DISSOLVE_STEP) {
        for (let x = 0; x < frame.width; x += DISSOLVE_STEP) {
          const pixel = scene.textures.getPixel(x, y, sprite.texture.key, frame.name);
          if (pixel === null || pixel.alpha === 0) continue;
          // A tinted layer is a white or grey mask: the tint scaled by the mask's own brightness.
          const shade = pixel.red / 255;
          const color =
            tint === null
              ? pixel.color
              : Phaser.Display.Color.GetColor(
                  Math.round(tint.red * shade),
                  Math.round(tint.green * shade),
                  Math.round(tint.blue * shade),
                );
          // Sprite origin is (0.5, 1): the frame's top-left sits half a width left and a full height up.
          const px = origin.x + x - Math.floor(frame.width / 2);
          const py = origin.y + y - frame.height;
          this.scatter(scene, px, py, color, depth, origin);
        }
      }
      sprite.setVisible(false);
    }
    this.label.setVisible(false);
    this.levelText.setVisible(false);
    this.huntMark.setVisible(false);
    this.titleText.setVisible(false);
    this.shine?.setVisible(false);
    this.rarityShine?.setVisible(false);
    this.rareMark?.setVisible(false);
    this.barBack.setVisible(false);
    this.barFill.setVisible(false);
    this.bubble.setVisible(false);
    this.statuses.clear();
  }

  private scatter(
    scene: Phaser.Scene,
    px: number,
    py: number,
    color: number,
    depth: number,
    origin: { x: number; y: number },
  ): void {
    const dot = scene.add.rectangle(px, py, 1, 1, color).setOrigin(0, 0).setDepth(depth);
    this.particles.push(dot);
    // Away from the body's centre, drifting up, like embers. Whole pixels only.
    const away = Math.sign(px - origin.x) || (Math.random() < 0.5 ? -1 : 1);
    const state = { x: px, y: py };
    const target = {
      x: px + away * (2 + Math.random() * 8),
      y: py - (4 + Math.random() * 10),
    };
    scene.tweens.add({
      targets: state,
      x: target.x,
      y: target.y,
      duration: DISSOLVE_MS * (0.6 + Math.random() * 0.4),
      ease: 'Quad.easeOut',
      onUpdate: (tween) => {
        dot.setPosition(Math.round(state.x), Math.round(state.y));
        dot.setAlpha(tween.progress < 0.5 ? 1 : 1 - (tween.progress - 0.5) * 2);
      },
      onComplete: () => {
        dot.destroy();
        const index = this.particles.indexOf(dot);
        if (index >= 0) this.particles.splice(index, 1);
      },
    });
  }

  /** Back from the dead (a respawn reuses the view): show the layers again and replay. */
  private revive(): void {
    this.dead = false;
    this.label.setVisible(true);
    this.levelText.setVisible(true);
    this.huntMark.setVisible(this.hunted);
    this.titleText.setVisible(true);
    this.shine?.setVisible(true);
    this.rarityShine?.setVisible(true);
    this.rareMark?.setVisible(true);
    // Forget the last pose so play() re-applies visibility and clips.
    this.animation = '';
    this.direction = null;
  }

  destroy(): void {
    this.bubbleTimer?.remove(false);
    this.marker?.timer.remove(false);
    this.clearAlert();
    this.flashTimer?.remove(false);
    this.freezeTimer?.remove(false);
    for (const dot of this.particles) dot.destroy();
    this.particles.length = 0;
    this.shine?.destroy();
    this.rarityShine?.destroy();
    this.rareMark?.destroy();
    this.statuses.destroy();
    this.barLayer?.destroy(true);
    this.container.destroy(true);
  }

  /** Half a tile down, so the character's feet sit on the tile's centre line. */
  static feetOffset(): number {
    return TILE_SIZE;
  }
}
